import React, { useEffect, useState } from 'react';
import { formatDate } from '@/utils/dateFormat';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Upload, 
  FileText, 
  Trash2, 
  Download, 
  Eye, 
  Building2, 
  MapPin, 
  User, 
  FileCheck,
  Loader2,
  Image as ImageIcon,
  Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import NotificationSettings from '@/components/NotificationSettings';

const TIPO_LABELS = {
  licenca_sanitaria: 'Licença Sanitária',
  alvara_funcionamento: 'Alvará de Funcionamento',
  coren_responsavel: 'COREN Responsável Técnico',
  outros: 'Outros Documentos'
};

export default function ConfiguracoesPage() {
  const [config, setConfig] = useState({
    nome_empresa: '',
    cnpj: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    telefone: '',
    email: '',
    responsavel_tecnico: '',
    crf_responsavel: '',
    documentos: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/configuracoes');
      if (response.data) {
        setConfig(response.data);
      }
    } catch (error) {
      console.log('Nenhuma configuração encontrada, criando nova...');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/configuracoes', config);
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e, tipo) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de arquivo não permitido. Use PDF, JPG ou PNG.');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 10MB.');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tipo', tipo);

      const response = await api.post('/configuracoes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setConfig(prev => ({
        ...prev,
        documentos: [...(prev.documentos || []), response.data]
      }));
      toast.success('Documento enviado com sucesso!');
      
      // Clear the input
      e.target.value = '';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Deseja excluir este documento?')) return;
    try {
      await api.delete(`/configuracoes/documento/${docId}`);
      setConfig(prev => ({
        ...prev,
        documentos: prev.documentos.filter(d => d.id !== docId)
      }));
      toast.success('Documento excluído!');
    } catch (error) {
      toast.error('Erro ao excluir documento');
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/configuracoes/documento/${doc.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nome);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar documento');
    }
  };

  const handlePreview = async (doc) => {
    // For images and PDFs, try to preview
    if (doc.content_type.startsWith('image/') || doc.content_type === 'application/pdf') {
      try {
        const response = await api.get(`/configuracoes/documento/${doc.id}`, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(new Blob([response.data], { type: doc.content_type }));
        setPreviewDoc({ ...doc, url });
        setShowPreview(true);
      } catch (error) {
        toast.error('Erro ao visualizar documento');
      }
    } else {
      // Download if not previewable
      handleDownload(doc);
    }
  };

  const formatCNPJ = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5').substring(0, 18);
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    return numbers.replace(/(\d{5})(\d{3})/, '$1-$2').substring(0, 9);
  };

  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 10) {
      return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3').substring(0, 15);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="configuracoes-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Configurações da Empresa
        </h1>
        <p className="text-slate-500 mt-1">Dados cadastrais e documentos</p>
      </div>

      {/* Notificações PWA */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Bell className="w-5 h-5 text-blue-600" />
          Notificações
        </h2>
        <NotificationSettings />
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Dados da Empresa */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            Dados da Empresa
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="nome_empresa">Nome da Empresa / Razão Social</Label>
              <Input
                id="nome_empresa"
                value={config.nome_empresa}
                onChange={(e) => setConfig({ ...config, nome_empresa: e.target.value })}
                placeholder="Sterisul Centro de Esterilização Ltda"
                data-testid="input-nome-empresa"
              />
            </div>
            <div>
              <Label htmlFor="cnpj">CNPJ</Label>
              <Input
                id="cnpj"
                value={config.cnpj}
                onChange={(e) => setConfig({ ...config, cnpj: formatCNPJ(e.target.value) })}
                placeholder="00.000.000/0000-00"
                maxLength={18}
                data-testid="input-cnpj"
              />
            </div>
            <div>
              <Label htmlFor="telefone">Telefone</Label>
              <Input
                id="telefone"
                value={config.telefone}
                onChange={(e) => setConfig({ ...config, telefone: formatPhone(e.target.value) })}
                placeholder="(00) 00000-0000"
                maxLength={15}
                data-testid="input-telefone"
              />
              <p className="text-xs text-slate-500 mt-1">Aparece no Portal do Cliente como contato (WhatsApp).</p>
            </div>
            <div className="col-span-2">
              <Label htmlFor="email">E-mail</Label>
              <Input
                id="email"
                type="email"
                value={config.email}
                onChange={(e) => setConfig({ ...config, email: e.target.value })}
                placeholder="contato@suaempresa.com.br"
                data-testid="input-email"
              />
            </div>
          </div>
        </div>

        {/* Endereço */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-blue-600" />
            Endereço
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label htmlFor="endereco">Endereço Completo</Label>
              <Input
                id="endereco"
                value={config.endereco}
                onChange={(e) => setConfig({ ...config, endereco: e.target.value })}
                placeholder="Rua, número, bairro"
                data-testid="input-endereco"
              />
            </div>
            <div>
              <Label htmlFor="cidade">Cidade</Label>
              <Input
                id="cidade"
                value={config.cidade}
                onChange={(e) => setConfig({ ...config, cidade: e.target.value })}
                placeholder="São Paulo"
                data-testid="input-cidade"
              />
            </div>
            <div>
              <Label htmlFor="estado">Estado</Label>
              <Input
                id="estado"
                value={config.estado}
                onChange={(e) => setConfig({ ...config, estado: e.target.value.toUpperCase() })}
                placeholder="SP"
                maxLength={2}
                data-testid="input-estado"
              />
            </div>
            <div>
              <Label htmlFor="cep">CEP</Label>
              <Input
                id="cep"
                value={config.cep}
                onChange={(e) => setConfig({ ...config, cep: formatCEP(e.target.value) })}
                placeholder="00000-000"
                maxLength={9}
                data-testid="input-cep"
              />
            </div>
          </div>
        </div>

        {/* Responsável Técnico */}
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-600" />
            Responsável Técnico
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="responsavel_tecnico">Nome Completo</Label>
              <Input
                id="responsavel_tecnico"
                value={config.responsavel_tecnico}
                onChange={(e) => setConfig({ ...config, responsavel_tecnico: e.target.value })}
                placeholder="Nome do responsável técnico"
                data-testid="input-responsavel"
              />
            </div>
            <div>
              <Label htmlFor="crf_responsavel">COREN / Registro Profissional</Label>
              <Input
                id="crf_responsavel"
                value={config.crf_responsavel}
                onChange={(e) => setConfig({ ...config, crf_responsavel: e.target.value })}
                placeholder="COREN-SP 000000"
                data-testid="input-coren"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            type="submit" 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            disabled={saving}
            data-testid="btn-salvar-config"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              'Salvar Configurações'
            )}
          </Button>
        </div>
      </form>

      {/* Upload de Documentos */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-blue-600" />
          Documentos e Licenças
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Faça upload de documentos importantes como licenças sanitárias, alvarás, etc. (PDF, JPG, PNG - máx. 10MB)
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {Object.entries(TIPO_LABELS).map(([tipo, label]) => (
            <label
              key={tipo}
              htmlFor={`upload-${tipo}`}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                uploading ? 'border-slate-200 bg-slate-50 cursor-not-allowed' : 'border-slate-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <Upload className={`w-8 h-8 mx-auto mb-2 ${uploading ? 'text-slate-300' : 'text-slate-400'}`} />
              <span className={`text-sm ${uploading ? 'text-slate-400' : 'text-slate-600'}`}>
                {label}
              </span>
              <Input
                id={`upload-${tipo}`}
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, tipo)}
                disabled={uploading}
                data-testid={`upload-${tipo}`}
              />
            </label>
          ))}
        </div>

        {uploading && (
          <div className="flex items-center justify-center gap-2 text-blue-600 mb-4">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Enviando documento...</span>
          </div>
        )}

        {/* Lista de Documentos */}
        {config.documentos && config.documentos.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">Documentos Enviados ({config.documentos.length})</h3>
            <div className="space-y-2">
              {config.documentos.map((doc) => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid={`documento-${doc.id}`}
                >
                  <div className="flex items-center gap-3">
                    {doc.content_type?.startsWith('image/') ? (
                      <ImageIcon className="w-6 h-6 text-green-600" />
                    ) : (
                      <FileText className="w-6 h-6 text-blue-600" />
                    )}
                    <div>
                      <div className="text-sm font-medium text-slate-900">{doc.nome}</div>
                      <div className="text-xs text-slate-500">
                        {TIPO_LABELS[doc.tipo] || doc.tipo} • {formatDate(doc.data_upload)}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreview(doc)}
                      className="text-slate-600 hover:text-blue-600"
                      title="Visualizar documento"
                      data-testid={`preview-${doc.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(doc)}
                      className="text-slate-600 hover:text-green-600"
                      title="Baixar documento"
                      data-testid={`download-${doc.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteDoc(doc.id)}
                      className="text-slate-600 hover:text-red-600"
                      title="Excluir documento"
                      data-testid={`delete-${doc.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!config.documentos || config.documentos.length === 0) && (
          <div className="text-center py-8 text-slate-400">
            <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Nenhum documento enviado ainda</p>
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5 text-blue-600" />
              {previewDoc?.nome}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto max-h-[70vh]">
            {previewDoc?.content_type === 'application/pdf' ? (
              <iframe
                src={previewDoc?.url}
                className="w-full h-[600px] border rounded"
                title="PDF Preview"
              />
            ) : previewDoc?.content_type?.startsWith('image/') ? (
              <img
                src={previewDoc?.url}
                alt={previewDoc?.nome}
                className="max-w-full h-auto mx-auto rounded"
              />
            ) : null}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Fechar
            </Button>
            <Button onClick={() => handleDownload(previewDoc)} className="bg-blue-600 hover:bg-blue-700">
              <Download className="w-4 h-4 mr-2" />
              Baixar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
